import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import React from "react";
import { useTranslation } from "react-i18next";
import ReactSelect, {
  ClassNamesConfig,
  components,
  DropdownIndicatorProps,
  GroupBase,
} from "react-select";

/** `compact` : barres de filtres (32 px) ; `row` : formulaires en ligne (30 px, comme les champs). */
export type SelectSize = "regular" | "compact" | "row";

type SelectOption = { value: string; label: string; isDisabled: boolean };

// Au-delà, on cherche plus vite en tapant qu'en faisant défiler. En dessous, la saisie
// ouvrirait le clavier du téléphone pour rien.
export const SEARCHABLE_FROM = 8;

/**
 * Gabarit et couleurs communs à toutes les listes, CityField compris. La liste est
 * dessinée par react-select : celle du navigateur surligne en bleu, sans moyen fiable de
 * changer cette couleur.
 */
const controlHeight: Record<SelectSize, string> = {
  regular: "!min-h-[42px]",
  compact: "!min-h-[32px] !text-sm",
  row: "!min-h-[30px] !text-sm",
};

export const selectClassNames = <Option,>({
  size,
  error,
}: {
  size: SelectSize;
  error: boolean;
}): ClassNamesConfig<Option, false, GroupBase<Option>> => ({
  control: (state) =>
    `${controlHeight[size]} !rounded-lg !border !shadow-none transition-colors ${
      error
        ? "!border-red-500"
        : state.isFocused
          ? "!border-tertiary"
          : "!border-grey-medium hover:!border-tertiary"
    } ${
      state.isDisabled
        ? "!bg-grey-light/60 !cursor-not-allowed"
        : "!bg-white !cursor-pointer"
    }`,
  valueContainer: () => (size === "regular" ? "!px-4" : "!py-0 !px-3"),
  singleValue: (state) =>
    state.isDisabled ? "!text-grey-readable" : "!text-default-text",
  placeholder: () => "!text-grey-readable",
  indicatorSeparator: () => "!hidden",
  dropdownIndicator: () => (size === "regular" ? "!px-4" : "!py-0 !px-3"),
  menu: () =>
    "!mt-1 !rounded-lg !border !border-grey-light !shadow-lg !overflow-hidden",
  menuList: () => "!py-1",
  menuPortal: () => "!z-[55]",
  option: (state) =>
    `${size === "regular" ? "!py-2" : "!py-1.5 !text-sm"} !px-4 ${
      state.isDisabled
        ? "!bg-white !text-grey-medium !cursor-not-allowed"
        : state.isSelected
          ? "!bg-secondary !text-tertiary !font-oxaniumMedium !cursor-pointer"
          : state.isFocused
            ? "!bg-secondary/20 !text-tertiary !cursor-pointer"
            : "!bg-white !text-default-text !cursor-pointer"
    }`,
});

export const SelectChevron = <Option,>(
  props: DropdownIndicatorProps<Option, false>
) => (
  <components.DropdownIndicator {...props}>
    <FontAwesomeIcon
      icon="chevron-down"
      className={`h-3 w-3 text-grey-medium transition-transform duration-200 ${
        props.selectProps.menuIsOpen ? "rotate-180" : ""
      }`}
    />
  </components.DropdownIndicator>
);

const textOf = (node: React.ReactNode): string =>
  React.Children.toArray(node)
    .map((child) =>
      typeof child === "string" || typeof child === "number"
        ? String(child)
        : ""
    )
    .join("");

const optionsOf = (children: React.ReactNode): SelectOption[] =>
  React.Children.toArray(children)
    .filter(React.isValidElement)
    .map((element) => {
      const props =
        element.props as React.OptionHTMLAttributes<HTMLOptionElement>;
      return {
        value: String(props.value ?? ""),
        label: textOf(props.children),
        isDisabled: Boolean(props.disabled),
      };
    });

type SelectProps = {
  value?: string | number;
  defaultValue?: string | number;
  onChange?: (event: React.ChangeEvent<HTMLSelectElement>) => void;
  name?: string;
  id?: string;
  required?: boolean;
  disabled?: boolean;
  "aria-label"?: string;
  size?: SelectSize;
  error?: boolean;
  className?: string;
  children: React.ReactNode;
};

/**
 * S'utilise comme un `<select>` natif : choix en `<option>`, `onChange` qui lit
 * `event.target.name` et `event.target.value`.
 */
export const Select = ({
  value,
  defaultValue,
  onChange,
  name = "",
  id,
  required,
  disabled,
  "aria-label": ariaLabel,
  size = "regular",
  error = false,
  className = "",
  children,
}: SelectProps) => {
  const { t } = useTranslation();
  const options = optionsOf(children);

  // L'option vide (« Sélectionner », « Tous ») sert de texte d'attente : tant qu'elle est
  // choisie, la liste n'a pas de valeur, et `required` bloque l'envoi comme en natif.
  const selected = (current: string | number | undefined) =>
    current === undefined
      ? undefined
      : (options.find(
          (option) => option.value === String(current) && option.value !== ""
        ) ?? null);

  return (
    <ReactSelect<SelectOption>
      className={className}
      inputId={id}
      name={name}
      required={required}
      isDisabled={disabled}
      isSearchable={options.length >= SEARCHABLE_FROM}
      noOptionsMessage={() => t("select-no-results")}
      aria-label={ariaLabel}
      options={options}
      value={selected(value)}
      defaultValue={selected(defaultValue)}
      placeholder={options.find((option) => option.value === "")?.label ?? ""}
      onChange={(option) =>
        onChange?.({
          target: { name, value: option?.value ?? "" },
        } as unknown as React.ChangeEvent<HTMLSelectElement>)
      }
      // Hors du flux : une modale à `overflow` masqué couperait la liste ouverte.
      menuPortalTarget={document.body}
      components={{ DropdownIndicator: SelectChevron }}
      classNames={selectClassNames<SelectOption>({ size, error })}
    />
  );
};
